import { Controller } from '@hotwired/stimulus'
import { flashDanger, uploadSizeError } from '../lib/flash'

// Cover image upload for events. Uses the same presigned-post flow as the
// document uploader: POST /api/attachments -> presigned S3 post -> store id.
export default class extends Controller {
  static targets = ['field', 'dropzone', 'input', 'removeBtn']
  static values = { url: String, attachmentId: String }

  connect () {
    this.emptyDropzoneHtml = this.dropzoneTarget.innerHTML
    if (this.urlValue) this.previewUrl(this.urlValue)
  }

  browse () {
    this.inputTarget.click()
  }

  async upload () {
    const file = this.inputTarget.files[0]
    if (!file) return

    const tooLarge = uploadSizeError(file, this.element)
    if (tooLarge) {
      flashDanger(tooLarge)
      this.inputTarget.value = ''
      return
    }

    this.dropzoneTarget.classList.add('is-uploading')

    try {
      const response = await fetch('/api/attachments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            filename: file.name,
            size: String(file.size),
            content_type: file.type,
            filepath: `/events/${crypto.randomUUID()}`
          }
        })
      })
      const { url, fields, id } = await response.json()

      const formData = new FormData()
      Object.entries(fields).forEach(([key, value]) => formData.append(key, value))
      formData.append('file', file)

      const s3Response = await fetch(url, { method: 'POST', body: formData })
      if (!s3Response.ok) throw new Error('S3 upload failed')

      this.fieldTarget.value = id
      this.attachmentIdValue = id
      this.preview(file)
    } catch (error) {
      console.error('Cover upload failed', error)
    } finally {
      this.dropzoneTarget.classList.remove('is-uploading')
    }
  }

  async remove () {
    const id = this.attachmentIdValue
    if (id) {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      fetch(`/attachments/${id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrf }
      }).catch(error => console.error('Cover removal failed', error))
    }

    this.fieldTarget.value = ''
    this.attachmentIdValue = ''
    this.resetDropzone()
  }

  preview (file) {
    this.previewUrl(URL.createObjectURL(file))
  }

  previewUrl (url) {
    const img = document.createElement('img')
    img.src = url
    this.dropzoneTarget.replaceChildren(img)
    this.dropzoneTarget.classList.add('has-preview')
    if (this.hasRemoveBtnTarget) this.removeBtnTarget.classList.add('is-visible')
  }

  resetDropzone () {
    this.dropzoneTarget.innerHTML = this.emptyDropzoneHtml
    this.dropzoneTarget.classList.remove('has-preview')
    if (this.hasRemoveBtnTarget) this.removeBtnTarget.classList.remove('is-visible')
    this.inputTarget.value = ''
  }
}
